<template>
  <b-card class="task-detail-general-section">
    <div class="task-information-wrapper">
      <h5 class="__title">
        {{ $t('fleetTaskInstant.title.assignmentInformation') }}
      </h5>

      <template v-if="isLoading">
        <b-skeleton width="85%" />
        <b-skeleton width="55%" />
      </template>

      <template v-else>
        <div class="__group">
          <i class="mc-icon icon-outline_car" />
          <span class="__label">{{ showResponse.vehicle.license_plate }}</span>
        </div>

        <div v-if="showResponse.driver.name != null" class="__group">
          <i class="mc-icon icon-outline_driver" />
          <span class="__label">{{ showResponse.driver.name }}</span>
          <i v-if="showResponse.driver.phone != null" class="__sub-label">{{
            `(${showResponse.driver.phone})`
          }}</i>
        </div>

        <div class="__group">
          <i class="mc-icon icon-outline_whatsapp" />
          <span class="__label">{{ showResponse.phone_number ?? '-' }}</span>
        </div>

        <div
          v-if="
            !isStatusEndedOrCancelled &&
            !isEditingNote &&
            !hasNote
          "
          class="note-section"
        >
          <div
            v-if="
              !isEditingNote &&
              !hasNote &&
              !isStatusEndedOrCancelled
            "
            class="__action-create"
            @click="isEditingNote = true"
          >
            <p class="m-0">
              {{ $t('fleetTaskInstant.action.addNote') }}
            </p>
            <Tooltip name="note-input">
              {{ $t('fleetTaskInstant.tooltip.note') }}
            </Tooltip>
            <i class="icon-outline_plus ms-auto" />
          </div>
        </div>

        <div v-else-if="isEditingNote || hasNote" class="note-section">
          <div class="__editing-section">
            <FormulateInput
              v-model="formValues.note"
              class="p-0"
              :label="$t('global.field.note')"
              type="textarea"
              :placeholder="$t('fleetTaskInstant.placeholder.note')"
              :tooltip="$t('fleetTaskInstant.tooltip.note')"
              :disabled="hasNote && !isEditingNote"
            />
            <div
              v-if="!isStatusEndedOrCancelled"
              class="__action-btns"
            >
              <template v-if="isEditingNote">
                <McButton
                  variant="secondary"
                  size="sm"
                  icon="icon-outline_close"
                  :text="$t('global.cancel')"
                  @click="handleCancelEditNote"
                />
                <McButton
                  size="sm"
                  icon="icon-outline_checkmark"
                  :text="$t('global.save')"
                  :disabled="formValues.note === null"
                  @click="handleSubmitNote"
                />
              </template>

              <McButton
                v-else
                variant="secondary"
                size="sm"
                :text="$t('global.edit')"
                icon="icon-outline_edit"
                @click="isEditingNote = true"
              />
            </div>
          </div>
        </div>
      </template>
    </div>

    <hr />

    <div class="point-section__wrapper">
      <div class="__title-wrapper">
        <h5 class="point-section__title">
          {{ $t('fleetTaskInstant.title.route') }}
        </h5>

        <SegmentedTabs
          :items="tabMappings"
          :activeTab="routeType"
          @update:activeTab="handleChangeTab"
        />
      </div>

      <b-skeleton-img v-if="isLoading || isLoadingPoint" no-aspect height="300px" />

      <template v-else>
        <div class="point-section__info">
          <p>Total:</p>
          <p><strong>{{ points.length }}</strong> {{ $t('global.text.destination') }}</p>
          <div class="__separator"></div>
          <p><strong>{{ (listPointRes?.total_distance / 1000) | formatNumber }}</strong> km</p>
          <div class="__separator"></div>
          <p>{{ formatDuration(listPointRes?.total_duration, true, { days: true, hours: true, minutes: true }) }}</p>
        </div>

        <div v-if="!isStatusEndedOrCancelled" class="late-notification-wrapper">
          <McToggle
            v-model="formValues.send_late_notification"
            class="d-flex"
            :disabled="isLoading"
            @change="handleChangeInput($event)"
          />
          <span>{{ formValues.send_late_notification ? $t('fleetTaskInstant.action.lateNotification.active') : $t('fleetTaskInstant.action.lateNotification.inactive') }}</span>
          <Tooltip :name="formValues.send_late_notification ? 'late-notification-active-state' : 'late-notification-not-active-state'" />
        </div>

        <div class="point-section__group">
          <div
            v-for="(point, index) in points"
            :key="point.id"
            class="point-section__list"
            :class="{ selected: selectedPointIdx == index }"
            @click="showDetailSection(index)"
          >
            <div class="__numbering-wrapper">
              <div
                class="__index"
                :style="{
                  backgroundColor: !['VISITING', 'VISITED'].includes(point.status.raw_type)
                    ? '#67718B'
                    : point.status.foreground_color,
                }"
              >
                <i
                  v-if="!isDefaultType(point.type)"
                  class="mc-icon"
                  :class="{
                    'icon-fill_flag': point.type === 'END',
                    'icon-fill_home': point.type === 'START',
                  }"
                />

                <template v-else-if="routeType === 'plan'">
                  {{ isAnyStartPoint ? index : index + 1 }}
                </template>

                <template v-else-if="routeType === 'actual'">
                  <div
                    v-if="!['VISITING', 'VISITED'].includes(point.status.raw_type)"
                    class="__no-number"
                  />

                  <template v-else>
                    {{ isAnyStartPoint ? index : index + 1 }}
                  </template>
                </template>
              </div>
              <div class="__dashed-line">
                <div
                  v-if="
                    ((points?.[index + 1]?.actual_distance && routeType == 'actual') || (points?.[index + 1]?.estimated_distance && routeType == 'plan')) &&
                    index !== points.length - 1
                  "
                  class="__point-distance"
                >
                  <div>{{ getDistance(points[index + 1]) | formatNumber }}</div>
                  <div>km</div>
                </div>
              </div>
            </div>

            <!-- Point Details -->
            <div class="point-detail">
              <div class="__address-wrapper">
                <div class="__title">
                  {{ point.address.name }}
                </div>
                <div
                  class="__status"
                  :style="{
                    backgroundColor: point.status.background_color,
                    color: point.status.foreground_color,
                  }"
                >
                  {{ computedStatus(point) }}
                </div>
              </div>
              <div class="__time-wrapper">
                <div v-if="isEndOrDefaultType(point.type)" class="__group">
                  <span class="__label">{{
                    isPointVisited(point.status.raw_type) ? $t('fleetTaskInstant.text.arrival') : $t('fleetTaskInstant.text.arrivalTarget')
                  }}</span>
                  <span class="__value">{{
                    isPointVisited(point.status.raw_type)
                      ? formatTime(point.actual_time_arrival)
                      : formatTime(point.target_time_arrival)
                  }}</span>
                  <span
                    v-if="isPointVisited(point.status.raw_type) && point.late_arrival_time"
                    class="__late-value"
                  >
                    {{ formatLateValues(point.late_arrival_time) }}
                  </span>
                </div>
                <div v-if="isStartOrDefaultType(point.type)" class="__group">
                  <span class="__label">{{
                    isPointVisited(point.status.raw_type) ? $t('fleetTaskInstant.text.departure') : $t('fleetTaskInstant.text.departureTarget')
                  }}</span>
                  <span class="__value">{{
                    isPointVisited(point.status.raw_type)
                      ? formatTime(point.actual_time_departure)
                      : formatTime(point.target_time_departure)
                  }}</span>
                  <span
                    v-if="isPointVisited(point.status.raw_type) && point.late_departure_time"
                    class="__late-value"
                  >
                    {{ formatLateValues(point.late_departure_time) }}
                  </span>
                </div>
              </div>
              <div v-if="point.service_time != null" class="__badge-wrapper">
                <div
                  v-if="point.activity_label.raw_type !== ''"
                  class="point-badge __activity-wrapper"
                >
                  {{ $t(`fleetTaskInstant.text.${point.activity_label.raw_type.toLowerCase()}`) }}
                </div>
                <div class="point-badge __service-wrapper">
                  <i class="mc-icon icon-outline_cube" />{{ formatValues(point.service_time) }}
                </div>
              </div>
              <template v-if="isValidProofStatus(index)">
                <template v-if="isEmpty(point.proof?.created_on)">
                  <div
                    class="__proof-wrapper clickable"
                    :class="{
                      error: proofErrors[point.id],
                    }"
                    @click.stop="handleOpenFilePicker(point.id)"
                  >
                    <div class="__title">
                      {{ $t('fleetTaskInstant.action.addAssignmentProof') }}
                    </div>
                    <i class="mc-icon icon-fill_plus" />
                  </div>
                </template>
                <div v-else class="__proof-wrapper">
                  <div>
                    <div class="__title">
                      {{ $t('fleetTaskInstant.action.viewAssignmentProof') }}
                    </div>
                    <div class="__subtitle">
                      {{ $t('fleetTaskInstant.text.createdOn', { time: formatDateWithTz(point.proof?.created_on, 'DD MMM YYYY, HH:mm') }) }}
                    </div>
                  </div>
                  <i class="mc-icon icon-outline_chevron-right" />
                </div>
              </template>
            </div>
          </div>
        </div>
      </template>
    </div>
    <input
      ref="fileInputRef"
      type="file"
      :accept="acceptFile"
      multiple
      hidden
      @change="handleAddProof"
    />
    <ConfirmAttachmentModal
      :attachments="attachments"
      @handle-upload-attachment="handleUploadAttachments"
    />
  </b-card>
</template>

<script>
import { mapState } from 'vuex'

import McButton from '@/components/McButton'
import McToggle from '@/components/McToggle'
import * as FleetTaskInstant from '@/services/rest-api/lib/fleet-task-instant/fleet-task-instant'
import { formatDateWithTz } from '@/helpers/tzDate'

import { formatValues, formatLateValues, formatDuration } from '../../../lib/helpers/formatValues'
import { EventBus } from '../../../lib/event-bus'
import { errorV2Msg } from '../../../lib/constants/error-v2-mapping'
import { statusIdToLabelMap } from '../../../lib/constants/point-status'
import ConfirmAttachmentModal from '../../../components/ConfirmAttachmentModal.vue'
import Tooltip from '../../../components/Tooltip.vue'
import SegmentedTabs from '../../../components/SegmentedTabs.vue'

import Compressor from 'compressorjs'
import cloneDeep from 'lodash/cloneDeep'
import { isEmpty } from 'lodash'

export default {
  components: {
    McButton,
    ConfirmAttachmentModal,
    McToggle,
    Tooltip,
    SegmentedTabs,
  },
  data() {
    return {
      isModalOpen: false,
      isLoading: true,
      isLoadingPoint: false,
      isEditingNote: false,

      errors: null,
      address: null,

      formValues: {
        note: null,
        send_late_notification: false,
      },
      proofErrors: {},
      maxImageFiles: 10,
      maxFileSize: 10 * 1024 * 1024, //10MB
      acceptFile: '.png, .jpg, .jpeg',
      selectedPointAttachmentIdx: null,

      attachments: [],
      statusIdToLabelMap,
    }
  },
  computed: {
    ...mapState('fleetTaskInstant', [
      'isLoadingGlobal',
      'showResponse',
      'listPointRes',
      'selectedPointIdx',
      'routeType',
    ]),
    points() {
      return this.listPointRes?.points || []
    },
    isAnyStartPoint() {
      return this.points[0]?.type === 'START'
    },
    hasActualRoute() {
      return this.points.some((point) => point.actual_sequence !== null)
    },
    hasNote() {
      return this.showResponse.note !== null && this.showResponse.note !== ''
    },
    tabMappings() {
      return [
        {
          key: 'actual',
          name: this.$t('fleetTaskInstant.text.actual'),
          disabled: this.isLoading || this.isLoadingPoint || !this.hasActualRoute,
          tooltip: this.$t('fleetTaskInstant.tooltip.actual'),
        },
        {
          key: 'plan',
          name: this.$t('fleetTaskInstant.text.plan'),
          disabled: this.isLoading || this.isLoadingPoint,
        },
      ]
    },
    isStatusEndedOrCancelled() {
      return ['ENDED', 'CANCELED'].includes(this.showResponse.status?.raw_type)
    },
    computedStatus() {
      return (item) => {
        const mappedStatus = this.statusIdToLabelMap[item.status?.raw_type]

        if (mappedStatus) {
          return this.$t(mappedStatus)
        }

        return item.status?.name
      }
    },
  },
  watch: {
    showResponse: {
      handler(newValue) {
        if (!(newValue.note === null || newValue.note === '' || this.isEditingNote)) {
          this.$set(this.formValues, 'note', newValue.note)
        }

        this.$set(this.formValues, 'send_late_notification', newValue.send_late_notification)
      },
      immediate: true,
    }
  },
  async created() {
    if (this.$route.params.id === undefined) return

    await this.getPointsData()
  },
  mounted() {
    EventBus.$on('get-points', this.getPointsData)
  },
  beforeDestroy() {
    EventBus.$off('get-points', this.getPointsData)

    this.$store.dispatch('fleetTaskInstant/setRouteType', 'actual')
  },
  methods: {
    formatValues,
    formatLateValues,
    formatDuration,
    formatDateWithTz,
    isEmpty,

    isPointVisited(status) {
      return status === 'VISITED'
    },

    isValidProofStatus(idx) {
      if (this.showResponse.status.raw_type === 'CANCELED') return false

      return ['NOT_VISITED', 'VISITING', 'VISITED'].includes(this.points[idx].status.raw_type)
    },

    isStartOrDefaultType(value) {
      return ['START', 'DEFAULT'].includes(value)
    },

    isEndOrDefaultType(value) {
      return ['END', 'DEFAULT'].includes(value)
    },

    isDefaultType(value) {
      return 'DEFAULT' === value
    },

    async showDetailSection(index) {
      this.$store.dispatch('fleetTaskInstant/setSelectedPointIdx', index)
      await this.$nextTick()

      EventBus.$emit('focus-point', true)

      if (this.isValidProofStatus(index)) {
        EventBus.$emit('get-point-detail')
      }
    },

    /**
     * Get list of points
     */
    async getPointsData(type = 'init', routeType = 'plan') {
      if (this.isLoading && type !== 'init') return

      let isOrderByActual = routeType === 'actual'
      if (type === 'periodic') {
        isOrderByActual = this.routeType === 'actual'
      }

      const res = await FleetTaskInstant.getPoints(this.$route.params.id, {
        is_order_by_actual: isOrderByActual,
      })

      if (res.errors) {
        this.errors = res.errors
      }

      this.$store.dispatch('fleetTaskInstant/setListPointRes', cloneDeep(res.data))
      EventBus.$emit('update-map', type)

      if (this.selectedPointIdx !== null) {
        EventBus.$emit('get-point-detail')
      }

      this.isLoading = false

      if (type === 'init') {
        if (!this.hasActualRoute) {
          this.$store.dispatch('fleetTaskInstant/setRouteType', 'plan')
          return
        }

        this.getPointsData('init-change-type', 'actual')
      }
    },

    handleOpenFilePicker(id) {
      this.selectedPointAttachmentIdx = id
      this.$refs.fileInputRef.click()
    },

    async handleAddProof(e) {
      try {
        const files = e.target.files
        //reset errors
        this.$set(this.proofErrors, this.selectedPointAttachmentIdx, false)

        // Validate file counts
        if (files.length > this.maxImageFiles) {
          this.$mcToast.error({
            component: 'McToastDynamicBody',
            props: {
              contents: [
                {
                  html: this.$t('fleetTaskInstant.toast.uploadAttachment.exceedMaxPhoto'),
                },
              ],
            },
          })
          this.$set(this.proofErrors, this.selectedPointAttachmentIdx, true)
          return
        }

        this.attachments = []
        for (let i = 0; i < files.length; i++) {
          const file = files[i]

          // Validate file type
          if (!['image/png', 'image/jpg', 'image/jpeg'].includes(file.type)) {
            this.$mcToast.error({
              component: 'McToastDynamicBody',
              props: {
                contents: [
                  {
                    html: this.$t('fleetTaskInstant.toast.uploadAttachment.invalidExt'),
                  },
                ],
              },
            })
            this.$set(this.proofErrors, this.selectedPointAttachmentIdx, true)
            return
          }

          // Validate file size
          if (file.size > this.maxFileSize) {
            this.$mcToast.error({
              component: 'McToastDynamicBody',
              props: {
                contents: [
                  {
                    html: this.$t('fleetTaskInstant.toast.uploadAttachment.exceedMaxDoc'),
                  },
                ],
              },
            })
            this.$set(this.proofErrors, this.selectedPointAttachmentIdx, true)
            return
          }

          const compressedFile = await new Promise((resolve, reject) => {
            new Compressor(file, {
              maxWidth: 1600,
              maxHeight: 1600,
              quality: 0.8,
              success: resolve,
              error: reject,
            })
          })

          this.attachments.push(compressedFile)
        }

        this.$bvModal.show('confirm-attachment-modal')
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Error selecting files:', err)
        }
        this.$mcToast.error(errorV2Msg['default']())
        this.$set(this.proofErrors, this.selectedPointAttachmentIdx, true)
        this.isLoadingPoint = false
      } finally {
        this.$refs.fileInputRef.value = null
      }
    },

    async handleUploadAttachments() {
      this.isLoadingPoint = true
      const res = await FleetTaskInstant.uploadAttachments(
        this.$route.params.id,
        this.selectedPointAttachmentIdx,
        this.attachments
      )

      if (res?.errors_v2) {
        const errorKey = res?.errors_v2.issues?.[0].code ?? ''
        let errorMsg = errorV2Msg[errorKey] ?? errorV2Msg['default']()

        if (typeof errorMsg === 'function') {
          errorMsg = errorMsg()
        }

        this.$mcToast.error({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              {
                html: errorMsg,
              },
            ],
          },
        })
        this.$set(this.proofErrors, this.selectedPointAttachmentIdx, true)
        this.isLoadingPoint = false
        return
      }

      this.$mcToast.success(this.$t('fleetTaskInstant.toast.uploadAttachment.success'))

      await this.getPointsData()
      this.isLoadingPoint = false
    },

    handleCancelEditNote() {
      this.isEditingNote = false
      this.$set(this.formValues, 'note', this.showResponse.note)
    },

    async handleSubmitNote() {
      if (this.isLoadingGlobal) return

      if (this.formValues.note === this.showResponse.note) {
        this.isEditingNote = false
        return
      }

      this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobal', true)

      const res = await FleetTaskInstant.update(this.$route.params.id, {
        note: this.formValues.note,
      })

      if (res.errors) {
        this.$mcToast.error('Terjadi kesalahan pada input Anda')
        this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobal', false)
        return
      }

      EventBus.$emit('refetch-fleet-task-instant', 'update-note')

      this.isEditingNote = false
      this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobal', false)
      this.$mcToast.success('Berhasil ubah catatan!')
    },

    formatTime(time) {
      if (!time) {
        return formatDateWithTz(time, 'HH:mm')
      }

      const today = new Date()
      today.setHours(0, 0, 0, 0)

      const timeDate = new Date(time)
      timeDate.setHours(0, 0, 0, 0)

      // If the date is today, only show time (HH:mm)
      if (timeDate.getTime() === today.getTime()) {
        return formatDateWithTz(time, 'HH:mm')
      }

      // Otherwise show full date and time (DD/MM/YY, HH:mm)
      return formatDateWithTz(time, 'DD/MM/YYYY, HH:mm')
    },

    getDistance(point) {
      let distance = point.estimated_distance
      if (this.routeType === 'actual') {
        distance = point.actual_distance
      }

      distance = distance / 1000

      return distance
    },

    async handleChangeTab(type, trigger) {
      this.$store.dispatch('fleetTaskInstant/setRouteType', type)

      if (trigger !== 'click') return
      
      await this.getPointsData('change-type', type)
      EventBus.$emit('set-trip-toggle', type)
    },
    
    async handleChangeInput(event) {
      this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobal', true)

      const res = await FleetTaskInstant.update(this.$route.params.id, {
        send_late_notification: event,
      })

      if (res.errors) {
        this.$mcToast.error('Terjadi kesalahan')
        this.formValues.send_late_notification = !event
        this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobal', false)
        return
      }

      sessionStorage.setItem(
        'quickAssignmentLateNotification', JSON.stringify(event))
      this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobal', false)
    },
  },
}
</script>
<style lang="scss">
@import 'style.scss';
</style>
